import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { rto, designMappings, inventory } from '../services/api';
import { useToast } from '../context/ToastContext';
import { Upload, RotateCcw, CheckCircle, X, Search, Loader2, Undo2, ScanLine, Camera, CameraOff, AlertTriangle, Keyboard, Trash2, Download, ImageIcon, Eye, Package, MapPin, Phone, Truck, Hash, PlusCircle, Smartphone, FileText } from 'lucide-react';

const MANUAL_ADD_SIZE_FALLBACK = ["XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL"];

// Backend sends naive UTC timestamps (no 'Z'/offset) — appending 'Z' here forces JS to
// parse them as UTC instead of guessing the viewer's local timezone, so the IST render
// below is correct regardless of where the browser is physically located.
const toUtcDate = (isoStr: string): Date => new Date(/[Zz]|[+-]\d\d:?\d\d$/.test(isoStr) ? isoStr : `${isoStr}Z`);

const formatIST = (isoStr: string, opts?: Intl.DateTimeFormatOptions) =>
  toUtcDate(isoStr).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', ...opts });

// Stable group-by-order: keeps a multi-item order's rows adjacent (so its
// "N of M items ready" badge is visible right where it matters) while
// preserving the original relative order both across and within groups.
const groupChecklistRowsByOrder = (rows: any[]): any[] => {
  const firstSeenAt: Record<string, number> = {};
  rows.forEach((r, i) => {
    const key = r.order_name || r.order_id || `__row${i}__`;
    if (!(key in firstSeenAt)) firstSeenAt[key] = i;
  });
  return rows
    .map((r, i) => ({ r, i, key: r.order_name || r.order_id || `__row${i}__` }))
    .sort((a, b) => (firstSeenAt[a.key] - firstSeenAt[b.key]) || (a.i - b.i))
    .map(x => x.r);
};

const RTO: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [assignItem, setAssignItem] = useState<any>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [assignAllLoading, setAssignAllLoading] = useState(false);
  const [selectedRtoIds, setSelectedRtoIds] = useState<Set<number>>(new Set());
  const [manualOrderId, setManualOrderId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // ═══ AUTO-MATCH STATE ═══
  const [matchMap, setMatchMap] = useState<Record<number, { ready_to_ship: string[]; pending: string[] }>>({});

  const handleIgnoreOrder = async (orderName: string) => {
    try {
      await rto.ignoreOrder(orderName);
      setMatchMap(prev => {
        const updated: Record<number, { ready_to_ship: string[]; pending: string[] }> = {};
        for (const rtoId of Object.keys(prev)) {
          const entry = prev[Number(rtoId)];
          const rts = entry.ready_to_ship.filter(o => o !== orderName);
          const pen = entry.pending.filter(o => o !== orderName);
          if (rts.length > 0 || pen.length > 0) {
            updated[Number(rtoId)] = { ready_to_ship: rts, pending: pen };
          }
        }
        return updated;
      });
      // Remove from suggestions in assign modal
      setSuggestions((prev: any[]) => prev.filter((s: any) => s.order_name !== orderName));
    } catch {}
  };
  const [matchLoading, setMatchLoading] = useState(false);

  // Filter state
  const [activeFilter, setActiveFilter] = useState<'all' | 'matched' | 'available' | 'used'>('all');
  const [stockSearch, setStockSearch] = useState('');
  // Tab state
  const [activeTab, setActiveTab] = useState<'assign' | 'stock' | 'category' | 'match' | 'history'>('assign');

  // ═══ HISTORY (added vs. used per day, by month) ═══
  const [historyMonth, setHistoryMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [historyData, setHistoryData] = useState<{ days: { date: string; added: number; used: number }[]; total_added: number; total_used: number; total_delivered: number; total_units_delivered: number; orders_delivered_used_rto: number; units_used_rto_delivered: number } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'history') return;
    setHistoryLoading(true);
    rto.getHistory(historyMonth).then(res => setHistoryData(res.data)).catch(() => setHistoryData(null)).finally(() => setHistoryLoading(false));
  }, [activeTab, historyMonth]);

  // ═══ MATCH UPLOAD (check a list of orders against available RTO stock) ═══
  // Uploading/matching happens once (e.g. on a Mac); the result is published as "the
  // current checklist" so every device on the same account — e.g. a staff phone —
  // picks it up automatically (polling) instead of re-uploading or transferring the file.
  const matchFileRef = useRef<HTMLInputElement>(null);
  const labelFileRef = useRef<HTMLInputElement>(null);
  const [matchUploadLoading, setMatchUploadLoading] = useState(false);
  const [matchUploadRows, setMatchUploadRows] = useState<any[]>([]);
  const [matchUploadSummary, setMatchUploadSummary] = useState<{ matched: number; total: number } | null>(null);
  const [matchUploadError, setMatchUploadError] = useState('');
  const [checklistCourier, setChecklistCourier] = useState<'delhivery' | 'nimbuspost'>('delhivery');
  const [matchDragActive, setMatchDragActive] = useState(false);
  const matchDragCounter = useRef(0);
  const [rowStates, setRowStates] = useState<Record<number, 'collected' | 'skipped'>>({});
  const [collectingRow, setCollectingRow] = useState<number | null>(null);
  const checklistLoadedRef = useRef(false);

  const applyChecklistData = (rows: any[], matched: number, total: number) => {
    setMatchUploadRows(rows);
    setMatchUploadSummary({ matched, total });
    const states: Record<number, 'collected' | 'skipped'> = {};
    rows.forEach((r, idx) => { if (r.status === 'collected' || r.status === 'skipped') states[idx] = r.status; });
    setRowStates(states);
  };

  const refreshSharedChecklist = useCallback(async () => {
    try {
      const res = await rto.getLatestChecklist();
      if (res.data.rows) applyChecklistData(res.data.rows, res.data.matched_count || 0, res.data.total || 0);
    } catch { /* silent — keep whatever is currently shown */ }
  }, []);

  // Load the shared checklist on first visit to this tab, then poll for updates
  // from other devices (new upload, or someone else collecting/skipping a row).
  useEffect(() => {
    if (activeTab !== 'match') return;
    if (!checklistLoadedRef.current) {
      checklistLoadedRef.current = true;
      refreshSharedChecklist();
    }
    const interval = setInterval(refreshSharedChecklist, 15000);
    return () => clearInterval(interval);
  }, [activeTab, refreshSharedChecklist]);

  const processMatchFile = async (file: File) => {
    setMatchUploadLoading(true);
    setMatchUploadError('');
    setMatchUploadRows([]);
    setMatchUploadSummary(null);
    setRowStates({});
    try {
      const res = await rto.matchUpload(file, checklistCourier);
      const rows = groupChecklistRowsByOrder(res.data.rows || []);
      const matched = res.data.matched_count || 0;
      const total = res.data.total || 0;
      applyChecklistData(rows, matched, total);
      rto.publishChecklist(rows, matched, total).catch(() => {});
    } catch (err: any) {
      setMatchUploadError(err.response?.data?.detail || 'Failed to process file');
    }
    setMatchUploadLoading(false);
  };

  const handleMatchUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    await processMatchFile(file);
    if (matchFileRef.current) matchFileRef.current.value = '';
  };

  const handleMatchDragEnter = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (matchUploadLoading || !e.dataTransfer.types.includes('Files')) return;
    matchDragCounter.current += 1;
    setMatchDragActive(true);
  };
  const handleMatchDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleMatchDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    matchDragCounter.current -= 1;
    if (matchDragCounter.current <= 0) {
      matchDragCounter.current = 0;
      setMatchDragActive(false);
    }
  };
  const handleMatchDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    matchDragCounter.current = 0;
    setMatchDragActive(false);
    if (matchUploadLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) await processMatchFile(file);
  };

  const handleCollect = async (row: any, idx: number) => {
    setCollectingRow(idx);
    try {
      await rto.assign(row.rto_item_id, row.order_name);
      setRowStates(prev => ({ ...prev, [idx]: 'collected' }));
      rto.setChecklistRowStatus(idx, 'collected').catch(() => {});
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to collect item');
    }
    setCollectingRow(null);
  };

  const [undoingRow, setUndoingRow] = useState<number | null>(null);
  const handleUndoCollect = async (row: any, idx: number) => {
    setUndoingRow(idx);
    try {
      await rto.unassign(row.rto_item_id);
      await rto.setChecklistRowStatus(idx, null);
      setRowStates(prev => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      load();
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to undo collect', 'error');
    }
    setUndoingRow(null);
  };

  const [removingRow, setRemovingRow] = useState<number | null>(null);
  const handleNotAvailable = async (idx: number) => {
    const row = matchUploadRows[idx];
    const wasMatched = !!row?.matched;
    if (wasMatched && !window.confirm('This RTO piece could not be found. Remove it from RTO stock? (You can undo this.)')) return;
    setRemovingRow(idx);
    try {
      await rto.setChecklistRowStatus(idx, 'skipped');
      setRowStates(prev => ({ ...prev, [idx]: 'skipped' }));
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to update item', 'error');
    }
    setRemovingRow(null);
  };

  const handleUndoNotAvailable = async (idx: number) => {
    setUndoingRow(idx);
    try {
      await rto.setChecklistRowStatus(idx, null);
      setRowStates(prev => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      load();
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to undo', 'error');
    }
    setUndoingRow(null);
  };
  const [printingChecklist, setPrintingChecklist] = useState(false);
  const handlePrintChecklist = async () => {
    setPrintingChecklist(true);
    try {
      const rows = matchUploadRows.map((r, idx) => ({ ...r, status: rowStates[idx] || null }));
      const res = await rto.printMatchList(rows);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'rto_check_list.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to generate PDF', 'error');
    }
    setPrintingChecklist(false);
  };

  const [printingEmbroideryNeeded, setPrintingEmbroideryNeeded] = useState(false);
  const handlePrintEmbroideryNeeded = async () => {
    setPrintingEmbroideryNeeded(true);
    try {
      const rows = matchUploadRows.map((r, idx) => ({ ...r, status: rowStates[idx] || null }));
      const res = await rto.printEmbroideryNeeded(rows);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'embroidery_needed.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to generate PDF', 'error');
    }
    setPrintingEmbroideryNeeded(false);
  };

  const [clearingChecklist, setClearingChecklist] = useState(false);
  const handleClearChecklist = async () => {
    if (!window.confirm('Remove this checklist for everyone? This clears it on all devices until a new file is uploaded.')) return;
    setClearingChecklist(true);
    try {
      await rto.clearChecklist();
      setMatchUploadRows([]);
      setMatchUploadSummary(null);
      setRowStates({});
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to remove checklist', 'error');
    }
    setClearingChecklist(false);
  };

  // ═══ REORDER SHIPPING LABELS (collected RTO orders first, by packing priority) ═══
  // Files are only ADDED to a pending list on upload/drop — nothing is sent to the
  // server until "Start Reordering" is clicked. This lets you drop Delhivery's split
  // exports (100-page cap) one at a time and combine them before processing, instead
  // of each drop firing its own separate (and therefore un-merged) reorder request.
  type ReorderKey = 'all' | 1 | 2 | 3 | 'embroidery';
  const REORDER_TIER_META: { key: ReorderKey; tier?: 1 | 2 | 3; label: string; filename: string }[] = [
    { key: 1, tier: 1, label: 'Ready to Pack', filename: 'shipping_labels_ready_to_pack.pdf' },
    { key: 2, tier: 2, label: 'Partial', filename: 'shipping_labels_partial.pdf' },
    { key: 3, tier: 3, label: 'Remaining', filename: 'shipping_labels_remaining.pdf' },
    { key: 'embroidery', label: 'Embroidery Labels', filename: 'shipping_labels_embroidery_needed.pdf' },
    { key: 'all', label: 'All (combined)', filename: 'shipping_labels_reordered.pdf' },
  ];
  const [reorderingLabels, setReorderingLabels] = useState(false);
  const [reorderedLabelUrls, setReorderedLabelUrls] = useState<Partial<Record<ReorderKey, string>>>({});
  const [reorderTierCounts, setReorderTierCounts] = useState<Record<1 | 2 | 3, number> & { embroidery?: number } | null>(null);
  const [labelDragActive, setLabelDragActive] = useState(false);
  const [pendingLabelFiles, setPendingLabelFiles] = useState<File[]>([]);
  const clearReorderedLabelUrls = () => {
    setReorderedLabelUrls(prev => {
      Object.values(prev).forEach(url => url && URL.revokeObjectURL(url));
      return {};
    });
  };
  const addPendingLabelFiles = (files: File[]) => {
    if (!files.length) return;
    clearReorderedLabelUrls();
    setPendingLabelFiles(prev => {
      const existing = new Set(prev.map(f => `${f.name}:${f.size}`));
      const toAdd = files.filter(f => !existing.has(`${f.name}:${f.size}`));
      return [...prev, ...toAdd];
    });
  };
  const removePendingLabelFile = (idx: number) => {
    setPendingLabelFiles(prev => prev.filter((_, i) => i !== idx));
  };
  const base64ToPdfBlob = (b64: string): Blob => {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: 'application/pdf' });
  };
  const handleStartReordering = async () => {
    if (!pendingLabelFiles.length) return;
    setReorderingLabels(true);
    try {
      // One upload, one backend scan — the 5 variants come back together in one
      // response. An earlier version POSTed the files separately per tier, which
      // quadrupled both the upload time and the server-side processing for the
      // same files.
      const [res, countsRes] = await Promise.all([
        rto.reorderLabels(pendingLabelFiles),
        rto.getReorderTierCounts().catch(() => null),
      ]);
      clearReorderedLabelUrls();
      setReorderedLabelUrls({
        all: URL.createObjectURL(base64ToPdfBlob(res.data.all)),
        1: URL.createObjectURL(base64ToPdfBlob(res.data['1'])),
        2: URL.createObjectURL(base64ToPdfBlob(res.data['2'])),
        3: URL.createObjectURL(base64ToPdfBlob(res.data['3'])),
        embroidery: URL.createObjectURL(base64ToPdfBlob(res.data.embroidery)),
      });
      if (countsRes) setReorderTierCounts({ 1: countsRes.data['1'] || 0, 2: countsRes.data['2'] || 0, 3: countsRes.data['3'] || 0, embroidery: countsRes.data['embroidery'] || 0 });
      toast(
        pendingLabelFiles.length > 1
          ? `${pendingLabelFiles.length} shipping label PDFs merged and reordered — ready to download`
          : 'Shipping labels reordered — ready to download',
        'success'
      );
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to reorder shipping labels', 'error');
    }
    setReorderingLabels(false);
  };
  const handleUploadLabels = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addPendingLabelFiles(files);
    e.target.value = '';
  };
  const handleLabelDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation(); // don't let this also reach the page-wide "Upload RTO Stock" dropzone
    setLabelDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    addPendingLabelFiles(files);
  };
  const handleDownloadReorderedLabels = (key: ReorderKey, filename: string) => {
    const url = reorderedLabelUrls[key];
    if (!url) return;
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
  };

  const [showActions, setShowActions] = useState(false);

  // Scan state
  const [showScan, setShowScan] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanPreview, setScanPreview] = useState<any>(null);
  const [scanError, setScanError] = useState('');
  const [scanConfirming, setScanConfirming] = useState(false);
  const [scanResult, setScanResult] = useState('');
  const [selectedScanItems, setSelectedScanItems] = useState<Set<string>>(new Set());
  const [scanMode, setScanMode] = useState<'camera' | 'manual' | 'photo'>('camera');
  const [cameraActive, setCameraActive] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const scannerRef = useRef<any>(null);
  const scannerContainerId = 'rto-barcode-scanner';

  // ═══ OCR PHOTO STATE ═══
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState<any>(null);
  const [ocrPreviewUrl, setOcrPreviewUrl] = useState<string>('');
  const [ocrShowRawText, setOcrShowRawText] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [photoStream, setPhotoStream] = useState<MediaStream | null>(null);
  const [photoCameraActive, setPhotoCameraActive] = useState(false);

  const load = () => {
    rto.getAll().then(r => setItems(r.data)).catch(() => {});
    rto.getStats().then(r => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const { toast } = useToast();

  // ═══ ADD MANUALLY (search design name → pick size → add to RTO stock) ═══
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualDesignQuery, setManualDesignQuery] = useState('');
  const [manualDesignList, setManualDesignList] = useState<any[]>([]);
  const [manualDesignLoading, setManualDesignLoading] = useState(false);
  const [manualSelectedDesign, setManualSelectedDesign] = useState<any>(null);
  const [manualSizeOptions, setManualSizeOptions] = useState<{ size: string; quantity: number }[]>([]);
  const [manualSizeLoading, setManualSizeLoading] = useState(false);
  const [manualAddingSize, setManualAddingSize] = useState<string | null>(null);
  const [manualJustAdded, setManualJustAdded] = useState<Record<string, number>>({});

  const openManualAdd = () => {
    setShowManualAdd(true);
    setManualDesignQuery('');
    setManualSelectedDesign(null);
    setManualSizeOptions([]);
    setManualJustAdded({});
    if (manualDesignList.length === 0) {
      setManualDesignLoading(true);
      designMappings.getAll().then(r => setManualDesignList(r.data || [])).catch(() => {}).finally(() => setManualDesignLoading(false));
    }
  };
  const closeManualAdd = () => {
    setShowManualAdd(false);
    setManualSelectedDesign(null);
    setManualSizeOptions([]);
  };
  const selectManualDesign = (design: any) => {
    setManualSelectedDesign(design);
    setManualJustAdded({});
    setManualSizeLoading(true);
    inventory.getItemSizes(design.blank_article, design.blank_color).then(r => {
      setManualSizeOptions((r.data || []).length > 0 ? r.data : MANUAL_ADD_SIZE_FALLBACK.map(s => ({ size: s, quantity: 0 })));
    }).catch(() => {
      setManualSizeOptions(MANUAL_ADD_SIZE_FALLBACK.map(s => ({ size: s, quantity: 0 })));
    }).finally(() => setManualSizeLoading(false));
  };
  const handleManualAddSize = async (size: string) => {
    if (!manualSelectedDesign) return;
    setManualAddingSize(size);
    try {
      await rto.addManual({
        article_type: manualSelectedDesign.blank_article,
        color: manualSelectedDesign.blank_color,
        size,
        design_name: manualSelectedDesign.shopify_title,
      });
      setManualJustAdded(prev => ({ ...prev, [size]: (prev[size] || 0) + 1 }));
      toast(`Added ${manualSelectedDesign.blank_article} · ${size} to RTO stock`, 'success');
      load();
    } catch (err: any) {
      toast(err.response?.data?.detail || 'Failed to add item', 'error');
    }
    setManualAddingSize(null);
  };
  const handleAddNextDesign = () => {
    setManualSelectedDesign(null);
    setManualSizeOptions([]);
    setManualDesignQuery('');
  };
  const filteredManualDesigns = manualDesignQuery.trim()
    ? manualDesignList.filter(d => d.shopify_title.toLowerCase().includes(manualDesignQuery.trim().toLowerCase()))
    : manualDesignList;

  // ═══ AUTO-MATCH ═══
  useEffect(() => {
    const availableItems = items.filter(i => i.status === 'available');
    if (availableItems.length === 0) { setMatchMap({}); return; }
    let cancelled = false;
    setMatchLoading(true);
    rto.suggestAll().then((res: any) => {
      if (cancelled) return;
      const raw: Record<string, { ready_to_ship: string[]; pending: string[] }> = res.data.matches || {};
      // Deduplicate: each order name used at most once across all RTO items (sorted by rtoId asc)
      const usedOrders = new Set<string>();
      const filteredMap: Record<number, { ready_to_ship: string[]; pending: string[] }> = {};
      const sortedIds = Object.keys(raw).map(Number).sort((a, b) => a - b);
      for (const rtoId of sortedIds) {
        const entry = raw[rtoId];
        const rts = (entry.ready_to_ship || []).filter(o => !usedOrders.has(o));
        rts.forEach(o => usedOrders.add(o));
        const pen = (entry.pending || []).filter(o => !usedOrders.has(o));
        pen.forEach(o => usedOrders.add(o));
        if (rts.length > 0 || pen.length > 0) {
          filteredMap[rtoId] = { ready_to_ship: rts, pending: pen };
        }
      }
      setMatchMap(filteredMap);
      setMatchLoading(false);
    }).catch(() => setMatchLoading(false));
    return () => { cancelled = true; };
  }, [items]);

  useEffect(() => {
    if (showScan && scanMode === 'manual' && scanInputRef.current) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [showScan, scanMode]);

  // ═══ BARCODE CAMERA ═══
  const startCamera = useCallback(async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      await new Promise(r => setTimeout(r, 200));
      const el = document.getElementById(scannerContainerId);
      if (!el) return;
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch (e) {}
        scannerRef.current = null;
      }
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 120 }, aspectRatio: 1.5 },
        (decodedText: string) => {
          setScanInput(decodedText);
          setCameraActive(false);
          try { scanner.stop(); } catch (e) {}
          scannerRef.current = null;
          handleScanLookupDirect(decodedText);
        },
        () => {}
      );
      setCameraActive(true);
    } catch (err: any) {
      console.error('Camera error:', err);
      setScanError('Camera not available. Use manual input instead.');
      setScanMode('manual');
    }
  }, []);

  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) {}
      scannerRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    if (showScan && scanMode === 'camera' && !scanPreview) { startCamera(); }
    if (scanMode !== 'camera') { stopCamera(); }
    return () => { stopCamera(); };
  }, [showScan, scanMode]);

  useEffect(() => { return () => { stopCamera(); stopPhotoCamera(); }; }, []);

  // ═══ PHOTO CAMERA (for OCR) ═══
  const startPhotoCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      setPhotoStream(stream);
      setPhotoCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Photo camera error:', err);
      setScanError('Camera not available. Use file upload instead.');
    }
  }, []);

  const stopPhotoCamera = useCallback(() => {
    if (photoStream) {
      photoStream.getTracks().forEach(t => t.stop());
      setPhotoStream(null);
    }
    setPhotoCameraActive(false);
  }, [photoStream]);

  // Start photo camera when switching to photo mode
  useEffect(() => {
    if (showScan && scanMode === 'photo' && !ocrResult && !ocrPreviewUrl) {
      startPhotoCamera();
    }
    if (scanMode !== 'photo') { stopPhotoCamera(); }
    return () => { stopPhotoCamera(); };
  }, [showScan, scanMode, ocrResult]);

  // Attach stream to video element when both are ready
  useEffect(() => {
    if (videoRef.current && photoStream) {
      videoRef.current.srcObject = photoStream;
      videoRef.current.play().catch(() => {});
    }
  }, [photoStream, photoCameraActive]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setOcrPreviewUrl(dataUrl);
    stopPhotoCamera();
    // Auto-trigger OCR
    processOCR(dataUrl);
  }, [photoStream]);

  const handlePhotoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setOcrPreviewUrl(dataUrl);
      stopPhotoCamera();
      processOCR(dataUrl);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const processOCR = async (imageBase64: string) => {
    setOcrLoading(true);
    setOcrResult(null);
    setScanError('');
    setScanResult('');
    try {
      const res = await rto.scanOCR(imageBase64);
      setOcrResult(res.data);
      if (!res.data.order_match && !res.data.extracted?.order_number) {
        setScanError('Could not find an order number on this label. Try a clearer photo or use manual input.');
      }
    } catch (e: any) {
      setScanError(e.response?.data?.detail || 'OCR processing failed. Make sure the label is clear and well-lit.');
    }
    setOcrLoading(false);
  };

  const handleOCRConfirm = async () => {
    if (!ocrResult?.order_match?.order_name) return;
    setScanConfirming(true);
    try {
      const res = await rto.scanOCRConfirm(ocrResult.order_match.order_name);
      setScanResult(res.data.message);
      setOcrResult(null);
      setOcrPreviewUrl('');
      load();
    } catch (e: any) {
      setScanError(e.response?.data?.detail || 'Failed to add RTO items');
    }
    setScanConfirming(false);
  };

  const resetOCR = () => {
    setOcrResult(null);
    setOcrPreviewUrl('');
    setOcrShowRawText(false);
    setScanError('');
    setScanResult('');
    if (scanMode === 'photo') {
      startPhotoCamera();
    }
  };

  const openAssign = async (item: any) => {
    setAssignItem(item);
    setManualOrderId('');
    setSuggestions([]);
    setSuggestLoading(true);
    try {
      const res = await rto.suggest(item.id);
      setSuggestions(res.data.matching_orders || []);
    } catch (e) {}
    setSuggestLoading(false);
  };

  const handleAssign = async (orderName: string) => {
    if (!assignItem) return;
    setAssigning(true);
    try {
      await rto.assign(assignItem.id, orderName);
      setAssignItem(null);
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Assignment failed');
    }
    setAssigning(false);
  };

  const handleUnassign = async (itemId: number) => {
    if (!window.confirm('Remove this RTO assignment?')) return;
    await rto.unassign(itemId);
    load();
  };

  const handleDelete = async (itemId: number) => {
    if (!window.confirm('Delete this RTO item permanently?')) return;
    try {
      await rto.delete(itemId);
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL ${items.length} RTO items permanently? This cannot be undone.`)) return;
    try {
      await rto.deleteAll();
      setSelectedRtoIds(new Set());
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Delete all failed');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRtoIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedRtoIds.size} selected RTO item${selectedRtoIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      await Promise.all(Array.from(selectedRtoIds).map(id => rto.delete(id)));
      setSelectedRtoIds(new Set());
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const [exportDate, setExportDate] = useState(() => new Date().toISOString().split('T')[0]);

  const handleExport = async () => {
    try {
      const res = await rto.exportAssigned(exportDate, exportDate);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rto_assigned_${exportDate}.xlsx`;
      a.click();
    } catch (e) {
      alert('Export failed');
    }
  };

  const handleExportMatched = async () => {
    try {
      const res = await rto.exportMatched();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rto_matched_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch {
      alert('Export failed');
    }
  };

  const handleScanLookupDirect = async (text: string) => {
    if (!text.trim()) return;
    setScanLoading(true);
    setScanError('');
    setScanPreview(null);
    setScanResult('');
    try {
      const res = await rto.scanLookup(text);
      setScanPreview(res.data);
    } catch (e: any) {
      setScanError(e.response?.data?.detail || 'Lookup failed');
    }
    setScanLoading(false);
  };

  const handleScanLookup = () => handleScanLookupDirect(scanInput);

  const handleScanConfirm = async () => {
    if (!scanInput.trim() || selectedScanItems.size === 0) return;
    setScanConfirming(true);
    try {
      const selected = scanPreview.items
        .filter((_: any, i: number) => selectedScanItems.has(`${i}`))
        .map((item: any) => ({ article_type: item.article_type, color: item.color, size: item.size }));
      const res = await rto.scanConfirm(scanInput, selected);
      setScanResult(res.data.message);
      setScanPreview(null);
      setScanInput('');
      setSelectedScanItems(new Set());
      load();
      if (scanMode === 'camera') {
        setTimeout(() => startCamera(), 500);
      } else {
        setTimeout(() => scanInputRef.current?.focus(), 200);
      }
    } catch (e: any) {
      setScanError(e.response?.data?.detail || 'Failed to add');
    }
    setScanConfirming(false);
  };

  const handleScanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); handleScanLookup(); }
  };

  const resetScan = () => {
    setScanInput('');
    setScanPreview(null);
    setScanError('');
    setScanResult('');
    setSelectedScanItems(new Set());
    setOcrResult(null);
    setOcrPreviewUrl('');
    setOcrShowRawText(false);
    if (scanMode === 'camera') {
      startCamera();
    } else if (scanMode === 'photo') {
      startPhotoCamera();
    } else {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const closeScan = () => {
    stopCamera();
    stopPhotoCamera();
    setShowScan(false);
    setScanPreview(null);
    setScanError('');
    setScanResult('');
    setScanInput('');
    setOcrResult(null);
    setOcrPreviewUrl('');
    setOcrShowRawText(false);
  };

  const statCards = [
    { label: 'Total RTO', value: stats.total ?? 0, color: 'var(--text-primary)' },
    { label: 'Available', value: stats.available ?? 0, color: 'var(--accent-green)' },
    { label: 'Assigned', value: stats.used ?? 0, color: 'var(--accent-blue)' },
  ];

  const matchCount = Object.keys(matchMap).length;

  // Filtered items based on active filter
  const filteredItems = items.filter(item => {
    if (activeFilter === 'matched') return !!matchMap[item.id];
    if (activeFilter === 'available') return item.status === 'available';
    if (activeFilter === 'used') return item.status === 'used';
    return true; // 'all'
  });

  const filterTabs = [
    { key: 'all' as const, label: 'All', count: items.length, color: 'var(--text-primary)', bg: 'var(--bg-elevated)', border: 'var(--border)' },
    { key: 'matched' as const, label: 'Matched', count: matchCount, color: '#f97316', bg: 'rgba(251,146,60,0.1)', border: 'rgba(251,146,60,0.25)' },
    { key: 'available' as const, label: 'Available', count: items.filter(i => i.status === 'available').length, color: 'var(--accent-green)', bg: 'var(--accent-green-soft)', border: 'rgba(45,212,160,0.25)' },
    { key: 'used' as const, label: 'Used', count: items.filter(i => i.status === 'used').length, color: 'var(--accent-blue)', bg: 'var(--accent-blue-soft)', border: 'rgba(79,125,249,0.25)' },
  ];

  return (
    <Layout title="RTO Stock" subtitle="Returned items — assign to new orders to skip inventory deduction">
    <div style={{ position: 'relative' }}>
      {/* ═══ PAGE HEADER ═══ */}
      <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 4, overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
          {([
            { key: 'assign' as const, label: 'Assign', count: matchCount },
            { key: 'stock' as const, label: 'Stock', count: items.length },
            { key: 'category' as const, label: 'By Category', count: new Set(items.filter((i: any) => i.status === 'available').map((i: any) => (i.article_type || '').trim() || 'Unknown')).size },
            { key: 'match' as const, label: 'Check List', count: matchUploadSummary?.matched ?? 0 },
            { key: 'history' as const, label: 'History', count: historyData?.total_added ?? 0 },
          ]).map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); if (tab.key === 'category') setActiveFilter('available'); }} style={{
              padding: '8px 20px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
              background: activeTab === tab.key ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'transparent',
              color: activeTab === tab.key ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.15s ease',
            }}>
              {tab.label}
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : 'var(--bg-elevated)',
                color: activeTab === tab.key ? '#fff' : 'var(--text-muted)',
              }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Right side buttons */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={openManualAdd} style={{
            padding: '9px 16px', background: 'var(--surface-2)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13,
            fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
          }}><PlusCircle size={14} /> Add Manually</button>

        </div>
      </div>

      {/* ═══ ASSIGN TAB ═══ */}
      {activeTab === 'assign' && (
        <div className="animate-in-1">
          {/* Assign All banner */}
          {matchCount > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(45,212,160,0.12), rgba(45,212,160,0.06))',
              border: '1px solid rgba(45,212,160,0.25)',
              borderRadius: 'var(--radius-md)', padding: '14px 20px', marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle size={18} color="var(--accent-green)" />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-green)' }}>
                    {matchCount} item{matchCount !== 1 ? 's' : ''} ready to assign
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                    Auto-matched to pending orders — inventory deduction skipped
                  </div>
                </div>
              </div>
              <button onClick={async () => {
                if (!window.confirm(`Assign all ${matchCount} matched RTO items?`)) return;
                setAssignAllLoading(true);
                try {
                  const res = await rto.assignAll();
                  const count = res.data?.assigned || 0;
                  alert(`${count} RTO item${count !== 1 ? 's' : ''} assigned!`);
                  window.location.reload();
                } catch { alert('Failed to assign all'); }
                setAssignAllLoading(false);
              }} disabled={assignAllLoading} style={{
                padding: '9px 20px', background: 'var(--accent-green)', color: '#fff', border: 'none',
                borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                opacity: assignAllLoading ? 0.6 : 1,
              }}>
                {assignAllLoading ? 'Assigning...' : `Assign All ${matchCount}`}
              </button>
            </div>
          )}

          {/* Matched items cards */}
          {matchCount > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {items.filter(item => !!matchMap[item.id]).map((item: any) => {
                const matchingOrdersEntry = matchMap[item.id];
                const matchingOrders = matchingOrdersEntry
                  ? [...matchingOrdersEntry.ready_to_ship, ...matchingOrdersEntry.pending]
                  : [];
                return (
                  <div key={item.id} style={{
                    background: 'var(--bg-card)', border: '1px solid rgba(45,212,160,0.25)',
                    borderRadius: 'var(--radius-md)', padding: '16px 18px',
                    boxShadow: '0 0 0 1px rgba(45,212,160,0.08)',
                  }}>
                    {/* Item identity */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 'var(--radius-sm)',
                        background: 'var(--accent-green-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 800, color: 'var(--accent-green)',
                      }}>{item.size}</div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{item.article_type}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.color}{item.design_name ? ` · ${item.design_name}` : ''}</div>
                      </div>
                    </div>

                    {/* Matched orders */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 6 }}>
                        Matched Orders
                      </div>
                      {matchingOrders.slice(0, 3).map((order: string) => (
                        <div key={order} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '5px 0', borderBottom: '1px solid var(--border)',
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-blue)' }}>{order}</span>
                          <button onClick={() => handleIgnoreOrder(order)} style={{
                            background: 'none', border: 'none', cursor: 'pointer', fontSize: 10.5,
                            color: 'var(--text-muted)', padding: '2px 6px', borderRadius: 4,
                          }}>Ignore</button>
                        </div>
                      ))}
                    </div>

                    {/* Assign button */}
                    <button onClick={() => openAssign(item)} style={{
                      width: '100%', padding: '10px', background: 'linear-gradient(135deg, var(--accent-green), #059669)',
                      border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                      <CheckCircle size={14} /> Assign →
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center', padding: '60px 20px',
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>No matches right now</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {items.filter(i => i.status === 'available').length > 0
                  ? `${items.filter(i => i.status === 'available').length} available items waiting for matching orders`
                  : 'Add RTO items via Upload or Add Manually'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ STOCK TAB ═══ */}
      {activeTab === 'stock' && (() => {
        const stockSearchQuery = stockSearch.trim().toLowerCase();
        const stockFilteredItems = items.filter((item: any) => {
          if (activeFilter === 'available' && item.status !== 'available') return false;
          if (activeFilter === 'used' && item.status !== 'used') return false;
          if (!stockSearchQuery) return true;
          return [item.article_type, item.color, item.size, item.design_name]
            .some((field: any) => typeof field === 'string' && field.toLowerCase().includes(stockSearchQuery));
        });
        return (
        <div className="animate-in-1">
          {/* Inline stats + filter row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            {/* Stat pills */}
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { label: 'Total', value: stats.total ?? 0, color: 'var(--text-primary)' },
                { label: 'Available', value: stats.available ?? 0, color: 'var(--accent-green)' },
                { label: 'Used', value: stats.used ?? 0, color: 'var(--accent-blue)' },
              ].map(s => (
                <div key={s.label} style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', padding: '6px 14px',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{s.label}</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: s.color }}>{s.value}</span>
                </div>
              ))}
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 4 }}>
              {([
                { key: 'all' as const, label: 'All', count: items.length },
                { key: 'available' as const, label: 'Available', count: items.filter(i => i.status === 'available').length, color: 'var(--accent-green)' },
                { key: 'used' as const, label: 'Used', count: items.filter(i => i.status === 'used').length, color: 'var(--accent-blue)' },
              ]).map(tab => (
                <button key={tab.key} onClick={() => setActiveFilter(tab.key as any)} style={{
                  padding: '6px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  background: activeFilter === tab.key ? 'var(--bg-elevated)' : 'var(--bg-card)',
                  border: `1px solid ${activeFilter === tab.key ? 'var(--accent-blue)' : 'var(--border)'}`,
                  color: activeFilter === tab.key ? (tab.color || 'var(--text-primary)') : 'var(--text-muted)',
                }}>
                  {tab.label}
                  <span style={{ fontSize: 10.5, fontWeight: 700 }}>{tab.count}</span>
                </button>
              ))}
            </div>

            {/* Search box */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 220px', maxWidth: 320,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '6px 12px',
            }}>
              <Search size={14} color="var(--text-muted)" />
              <input
                value={stockSearch}
                onChange={e => setStockSearch(e.target.value)}
                placeholder="Search article, color, size, or design..."
                style={{
                  flex: 1, border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 12.5, color: 'var(--text-primary)',
                }}
              />
              {stockSearch && (
                <X size={14} color="var(--text-muted)" style={{ cursor: 'pointer' }} onClick={() => setStockSearch('')} />
              )}
            </div>

            {selectedRtoIds.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                style={{
                  padding: '6px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', background: 'var(--bg-card)',
                  border: '1px solid var(--accent-red, #ef4444)',
                  color: 'var(--accent-red, #ef4444)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Trash2 size={13} />
                Delete {selectedRtoIds.size}
              </button>
            )}
            {items.length > 0 && (
              <button
                onClick={handleDeleteAll}
                style={{
                  padding: '6px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', background: 'var(--bg-card)',
                  border: '1px solid var(--accent-red, #ef4444)',
                  color: 'var(--accent-red, #ef4444)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Trash2 size={13} />
                Delete All
              </button>
            )}
          </div>

          {/* Table */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <div className="table-responsive">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '12px 16px', width: 36 }}>
                      {(() => {
                        const visibleIds = stockFilteredItems.map((i: any) => i.id);
                        const allSelected = visibleIds.length > 0 && visibleIds.every((id: number) => selectedRtoIds.has(id));
                        return (
                          <input type="checkbox" checked={allSelected} onChange={() => {
                            if (allSelected) {
                              setSelectedRtoIds(prev => { const s = new Set(prev); visibleIds.forEach((id: number) => s.delete(id)); return s; });
                            } else {
                              setSelectedRtoIds(prev => { const s = new Set(prev); visibleIds.forEach((id: number) => s.add(id)); return s; });
                            }
                          }} style={{ cursor: 'pointer' }} />
                        );
                      })()}
                    </th>
                    {['Article', 'Color', 'Size', 'Design', 'Received', 'Status', 'Assigned To', ''].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stockFilteredItems.map((item: any) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', background: selectedRtoIds.has(item.id) ? 'var(--accent-red-soft, rgba(239,68,68,0.05))' : undefined }}>
                      <td style={{ padding: '12px 16px', width: 36 }}>
                        <input type="checkbox" checked={selectedRtoIds.has(item.id)} onChange={() => {
                          setSelectedRtoIds(prev => { const s = new Set(prev); s.has(item.id) ? s.delete(item.id) : s.add(item.id); return s; });
                        }} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{item.article_type}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{item.color}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '3px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 600 }}>{item.size}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{item.design_name || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }} title={`${formatIST(item.date_received)} IST`}>
                        {formatIST(item.date_received, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        <span style={{ color: 'var(--text-muted)', opacity: 0.7 }}> {formatIST(item.date_received, { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          background: item.status === 'available' ? 'var(--accent-green-soft)' : 'var(--accent-blue-soft)',
                          color: item.status === 'available' ? 'var(--accent-green)' : 'var(--accent-blue)',
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                        }}>{item.status}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12.5 }}>
                        {item.assigned_order_name
                          ? <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{item.assigned_order_name}</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {item.status === 'available' && (
                            <button onClick={() => openAssign(item)} style={{
                              background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.15)',
                              borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
                              color: 'var(--accent-green)', fontSize: 11, fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: 4,
                            }}><CheckCircle size={11} /> Assign</button>
                          )}
                          {item.status === 'used' && item.assigned_order_name && (
                            <button onClick={() => handleUnassign(item.id)} style={{
                              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                              borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
                              color: 'var(--text-muted)', fontSize: 11, fontWeight: 500,
                              display: 'flex', alignItems: 'center', gap: 4,
                            }}><Undo2 size={11} /> Undo</button>
                          )}
                          <button onClick={() => handleDelete(item.id)} style={{
                            background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.15)',
                            borderRadius: 6, padding: '5px 8px', cursor: 'pointer',
                            color: 'var(--accent-red)', display: 'flex', alignItems: 'center',
                          }}><Trash2 size={11} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {stockFilteredItems.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
                {stockSearchQuery ? 'No items match your search.' : activeFilter === 'available' ? 'No available items.' : activeFilter === 'used' ? 'No used items.' : 'No RTO items yet.'}
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* ═══ BY CATEGORY TAB ═══ */}
      {activeTab === 'category' && (() => {
        const categoryItems = items.filter((item: any) => {
          if (activeFilter === 'available' && item.status !== 'available') return false;
          if (activeFilter === 'used' && item.status !== 'used') return false;
          return true;
        });
        const groups: Record<string, { count: number; bySize: Record<string, number> }> = {};
        categoryItems.forEach((item: any) => {
          const cat = (item.article_type || '').trim() || 'Unknown';
          if (!groups[cat]) groups[cat] = { count: 0, bySize: {} };
          groups[cat].count += 1;
          const size = (item.size || '').trim() || '—';
          groups[cat].bySize[size] = (groups[cat].bySize[size] || 0) + 1;
        });
        const sortedCategories = Object.entries(groups).sort((a, b) => b[1].count - a[1].count);

        return (
          <div className="animate-in-1">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                <strong style={{ color: 'var(--text-primary)', fontSize: 15 }}>{categoryItems.length}</strong> item{categoryItems.length !== 1 ? 's' : ''} across <strong style={{ color: 'var(--text-primary)' }}>{sortedCategories.length}</strong> categor{sortedCategories.length !== 1 ? 'ies' : 'y'}
              </div>
              {/* Filter — shared with the Stock tab's Available/Used/All filter */}
              <div style={{ display: 'flex', gap: 4 }}>
                {([
                  { key: 'all' as const, label: 'All', color: 'var(--text-primary)' },
                  { key: 'available' as const, label: 'Available', color: 'var(--accent-green)' },
                  { key: 'used' as const, label: 'Used', color: 'var(--accent-blue)' },
                ]).map(tab => (
                  <button key={tab.key} onClick={() => setActiveFilter(tab.key as any)} style={{
                    padding: '6px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    background: activeFilter === tab.key ? 'var(--bg-elevated)' : 'var(--bg-card)',
                    border: `1px solid ${activeFilter === tab.key ? 'var(--accent-blue)' : 'var(--border)'}`,
                    color: activeFilter === tab.key ? tab.color : 'var(--text-muted)',
                  }}>{tab.label}</button>
                ))}
              </div>
            </div>

            {sortedCategories.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                No RTO items {activeFilter === 'available' ? 'available' : activeFilter === 'used' ? 'used' : 'yet'}.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
                {sortedCategories.map(([cat, data]) => (
                  <div
                    key={cat}
                    onClick={() => { setStockSearch(cat); setActiveTab('stock'); }}
                    title={`View "${cat}" in Stock`}
                    style={{
                      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
                      padding: '16px 18px', cursor: 'pointer', transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-blue)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{cat}</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent-blue)', flexShrink: 0 }}>{data.count}</div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {Object.entries(data.bySize).sort((a, b) => b[1] - a[1]).map(([size, n]) => (
                        <span key={size} style={{
                          background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6,
                          padding: '2px 8px', fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600,
                        }}>{size}: {n}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ CHECK LIST TAB ═══ */}
      {activeTab === 'match' && (
        <div className="animate-in-1">
          <div
            onDragEnter={handleMatchDragEnter}
            onDragOver={handleMatchDragOver}
            onDragLeave={handleMatchDragLeave}
            onDrop={handleMatchDrop}
            style={{
              background: matchDragActive ? 'rgba(79,125,249,0.08)' : 'var(--bg-card)',
              border: matchDragActive ? '2px dashed var(--accent-blue)' : '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: 28, marginBottom: 20, transition: 'border 0.15s, background 0.15s',
            }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Check Orders Against RTO Stock</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.6 }}>
              {matchDragActive ? 'Drop the file anywhere in this card' : (
                <>Upload a raw Delhivery or Nimbuspost shipment export (pick which below) — or a flat Excel/CSV with <strong>order id</strong>, <strong>order name</strong> and <strong>product name</strong> columns. Each product is matched against RTO stock currently available by design name and size. Drag & drop works too.</>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 4, width: 'fit-content' }}>
              {(['delhivery', 'nimbuspost'] as const).map(c => (
                <button key={c} onClick={() => setChecklistCourier(c)} disabled={matchUploadLoading} style={{
                  padding: '7px 16px', borderRadius: 'var(--radius-sm)', border: 'none', fontSize: 12.5, fontWeight: 700,
                  cursor: matchUploadLoading ? 'default' : 'pointer', textTransform: 'capitalize',
                  background: checklistCourier === c ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'transparent',
                  color: checklistCourier === c ? '#fff' : 'var(--text-muted)',
                }}>{c}</button>
              ))}
            </div>
            {/* No `accept` filter: some OS file dialogs grey out formats like .numbers
                or .pdf when an accept list is set; the server validates the file instead. */}
            <input type="file" ref={matchFileRef} onChange={handleMatchUpload} style={{ display: 'none' }} />
            <button onClick={() => matchFileRef.current?.click()} disabled={matchUploadLoading} style={{
              padding: '12px 22px', background: matchUploadLoading ? 'var(--bg-elevated)' : 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))',
              border: 'none', borderRadius: 'var(--radius-sm)', color: matchUploadLoading ? 'var(--text-muted)' : '#fff', fontSize: 14,
              fontWeight: 700, cursor: matchUploadLoading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 9,
            }}>
              {matchUploadLoading ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Matching...</> : <><Upload size={16} /> Upload Excel</>}
            </button>
            {matchUploadError && (
              <div style={{ marginTop: 14, background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.15)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 12.5, color: 'var(--accent-red)', fontWeight: 500 }}>{matchUploadError}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 16, fontSize: 12, color: 'var(--text-muted)' }}>
              <Smartphone size={13} style={{ flexShrink: 0 }} />
              Uploading here also updates the Check List on every other device signed in — no need to re-upload on your phone.
            </div>
          </div>

          {matchUploadSummary && (
            // Drag handlers (with stopPropagation) live on this whole card, not just the small
            // "Upload Shipping Labels" button — a tiny button is an easy target to miss while
            // dragging, and anything that falls through lands on the page-wide "Upload RTO
            // Stock" dropzone instead, which rejects shipping-label PDFs as "not a recognizable
            // table." Making the whole card a drop target makes that mistake much harder to hit.
            <div
              onDragEnter={e => { e.preventDefault(); e.stopPropagation(); if (!reorderingLabels) setLabelDragActive(true); }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!reorderingLabels) setLabelDragActive(true); }}
              onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setLabelDragActive(false); }}
              onDrop={handleLabelDrop}
              style={{
              background: labelDragActive ? 'rgba(79,125,249,0.1)' : matchUploadSummary.matched > 0 ? 'linear-gradient(135deg, rgba(45,212,160,0.12), rgba(45,212,160,0.06))' : 'var(--bg-card)',
              border: labelDragActive ? '2px dashed var(--accent-blue)' : matchUploadSummary.matched > 0 ? '1px solid rgba(45,212,160,0.25)' : '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
              transition: 'border 0.15s, background 0.15s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle size={18} color={matchUploadSummary.matched > 0 ? 'var(--accent-green)' : 'var(--text-muted)'} />
                <div style={{ fontSize: 14, fontWeight: 700, color: matchUploadSummary.matched > 0 ? 'var(--accent-green)' : 'var(--text-primary)' }}>
                  {labelDragActive ? 'Drop PDFs anywhere in this card' : `${matchUploadSummary.matched} of ${matchUploadSummary.total} uploaded order${matchUploadSummary.total !== 1 ? 's' : ''} available in RTO stock`}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 6, textAlign: 'right' }}>
                    Shipping Labels
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* Plain div (not <label>) — Safari doesn't reliably fire drag/drop events on a
                        <label> wrapping a file input, since the label's native click-forwarding
                        behavior can swallow them. Click is wired up explicitly instead. Clicking/
                        dropping here only ADDS to the pending list below — nothing is sent to the
                        server until "Start Reordering" is clicked, so Delhivery's split exports
                        (100-page cap) can be added one at a time and combined first. */}
                    <div
                      onClick={() => !reorderingLabels && labelFileRef.current?.click()}
                      style={{
                        padding: '8px 16px', background: 'var(--bg-elevated)',
                        border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)',
                        color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 600, cursor: reorderingLabels ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 7, opacity: reorderingLabels ? 0.6 : 1,
                      }}>
                      <input type="file" ref={labelFileRef} accept="application/pdf" multiple onChange={handleUploadLabels} disabled={reorderingLabels} style={{ display: 'none' }} />
                      <Upload size={14} />
                      Add Shipping Label PDF{pendingLabelFiles.length > 0 ? 's' : ''}
                    </div>
                    <button onClick={handleStartReordering} disabled={!pendingLabelFiles.length || reorderingLabels} style={{
                      padding: '8px 16px', background: pendingLabelFiles.length ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'var(--bg-elevated)',
                      border: 'none', borderRadius: 'var(--radius-sm)', color: pendingLabelFiles.length ? '#fff' : 'var(--text-muted)', fontSize: 12.5,
                      fontWeight: 700, cursor: (!pendingLabelFiles.length || reorderingLabels) ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 7, opacity: reorderingLabels ? 0.6 : 1,
                    }}>
                      {reorderingLabels ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <ScanLine size={14} />}
                      {reorderingLabels ? 'Reordering...' : `Start Reordering${pendingLabelFiles.length ? ` (${pendingLabelFiles.length})` : ''}`}
                    </button>
                    {REORDER_TIER_META.filter(m => reorderedLabelUrls[m.key]).map(m => {
                      const count = m.tier ? reorderTierCounts?.[m.tier] : (m.key === 'embroidery' ? reorderTierCounts?.embroidery : undefined);
                      const isEmbroidery = m.key === 'embroidery';
                      return (
                        <button key={m.key} onClick={() => handleDownloadReorderedLabels(m.key, m.filename)}
                          title={isEmbroidery ? 'Orders needing fresh embroidery — pulled out of the tier PDFs' : undefined}
                          style={{
                            padding: '8px 16px',
                            background: isEmbroidery ? 'rgba(124,58,237,0.08)' : 'linear-gradient(135deg, rgba(45,212,160,0.15), rgba(45,212,160,0.08))',
                            border: isEmbroidery ? '1px solid rgba(124,58,237,0.3)' : '1px solid rgba(45,212,160,0.3)',
                            borderRadius: 'var(--radius-sm)',
                            color: isEmbroidery ? '#7c3aed' : 'var(--accent-green)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 7,
                          }}>
                          <Download size={14} /> {m.label}{count !== undefined ? ` (${count})` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 6, textAlign: 'right' }}>
                    Checklist
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button onClick={handlePrintChecklist} disabled={printingChecklist} style={{
                      padding: '8px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                      color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 600, cursor: printingChecklist ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 7, opacity: printingChecklist ? 0.6 : 1,
                    }}>
                      {printingChecklist ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
                      {printingChecklist ? 'Generating...' : 'Print PDF'}
                    </button>
                    <button onClick={handlePrintEmbroideryNeeded} disabled={printingEmbroideryNeeded} style={{
                      padding: '8px 16px', background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 'var(--radius-sm)',
                      color: '#7c3aed', fontSize: 12.5, fontWeight: 600, cursor: printingEmbroideryNeeded ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 7, opacity: printingEmbroideryNeeded ? 0.6 : 1,
                    }} title="Embroidery items with no RTO match — need to be freshly made">
                      {printingEmbroideryNeeded ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
                      {printingEmbroideryNeeded ? 'Generating...' : 'Embroidery Needed'}
                    </button>
                    <div style={{ width: 1, background: 'var(--border)', margin: '0 2px' }} />
                    <button onClick={handleClearChecklist} disabled={clearingChecklist} style={{
                      padding: '8px 16px', background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.2)', borderRadius: 'var(--radius-sm)',
                      color: 'var(--accent-red)', fontSize: 12.5, fontWeight: 600, cursor: clearingChecklist ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 7, opacity: clearingChecklist ? 0.6 : 1,
                    }}>
                      {clearingChecklist ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={14} />}
                      {clearingChecklist ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
              </div>
              {pendingLabelFiles.length > 0 && (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                  {pendingLabelFiles.map((f, idx) => (
                    <div key={`${f.name}-${f.size}-${idx}`} style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FileText size={12} style={{ flexShrink: 0 }} />
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{f.name}</span>
                      {!reorderingLabels && (
                        <button onClick={() => removePendingLabelFile(idx)} title="Remove" style={{
                          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                          display: 'flex', alignItems: 'center', padding: 2,
                        }}><X size={12} /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {matchUploadRows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {matchUploadRows.map((row, idx) => {
                const state = rowStates[idx];
                const isMatched = row.matched;
                const isCollecting = collectingRow === idx;
                const isRemoving = removingRow === idx;
                return (
                  <div key={idx} style={{
                    background: isMatched ? 'linear-gradient(135deg, rgba(45,212,160,0.10), rgba(45,212,160,0.04))' : 'var(--bg-card)',
                    border: isMatched ? '1px solid rgba(45,212,160,0.28)' : '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', padding: '14px 18px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
                  }}>
                    <div style={{ minWidth: 220 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {row.order_name || row.order_id || '—'}
                        {row.order_total_items > 1 && (
                          <span style={{
                            fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                            background: row.order_ready_items === row.order_total_items ? 'rgba(45,212,160,0.15)' : 'rgba(217,119,6,0.15)',
                            color: row.order_ready_items === row.order_total_items ? 'var(--accent-green)' : '#d97706',
                          }}>
                            {row.order_ready_items === row.order_total_items
                              ? `All ${row.order_total_items} items ready`
                              : `${row.order_ready_items} of ${row.order_total_items} items ready`}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{row.product_name}</div>
                    </div>

                    {isMatched ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 12, color: 'var(--accent-green)', fontWeight: 600 }}>
                          Matched: {row.rto_detail?.article_type} · {row.rto_detail?.color} · {row.rto_detail?.size}
                          {row.rto_detail?.design_name ? ` · ${row.rto_detail.design_name}` : ''}
                          {row.rto_detail?.date_received ? (
                            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                              {' · Added '}
                              {formatIST(row.rto_detail.date_received, { day: 'numeric', month: 'short', year: 'numeric' })}
                              {' ('}{Math.max(0, Math.floor((Date.now() - toUtcDate(row.rto_detail.date_received).getTime()) / 86400000))}{'d ago)'}
                            </span>
                          ) : null}
                        </div>
                        {state === 'collected' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-green)', display: 'flex', alignItems: 'center', gap: 5 }}><CheckCircle size={14} /> Collected</span>
                            <button onClick={() => handleUndoCollect(row, idx)} disabled={undoingRow === idx} style={{
                              padding: '6px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                              color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 600, cursor: undoingRow === idx ? 'default' : 'pointer',
                              opacity: undoingRow === idx ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 5,
                            }}><Undo2 size={12} /> {undoingRow === idx ? 'Undoing...' : 'Undo'}</button>
                          </div>
                        ) : state === 'skipped' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-red)', display: 'flex', alignItems: 'center', gap: 5 }}><AlertTriangle size={14} /> Not Available</span>
                            <button onClick={() => handleUndoNotAvailable(idx)} disabled={undoingRow === idx} style={{
                              padding: '6px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                              color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 600, cursor: undoingRow === idx ? 'default' : 'pointer',
                              opacity: undoingRow === idx ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 5,
                            }}><Undo2 size={12} /> {undoingRow === idx ? 'Undoing...' : 'Undo'}</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleCollect(row, idx)} disabled={isCollecting || isRemoving} style={{
                              padding: '8px 16px', background: 'var(--accent-green)', border: 'none', borderRadius: 'var(--radius-sm)',
                              color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: isCollecting ? 'default' : 'pointer', opacity: isCollecting || isRemoving ? 0.6 : 1,
                            }}>{isCollecting ? 'Collecting...' : 'Collect'}</button>
                            <button onClick={() => handleNotAvailable(idx)} disabled={isCollecting || isRemoving} style={{
                              padding: '8px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                              color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 600, cursor: isRemoving ? 'default' : 'pointer', opacity: isRemoving ? 0.6 : 1,
                            }}>{isRemoving ? 'Removing...' : 'Not Available'}</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.reason || 'No match'}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="animate-in-1">
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 28, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>RTO Added vs. Used, by Day</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Added = RTO pieces received this month. Used = RTO pieces assigned to an order.</div>
              </div>
              <input
                type="month"
                value={historyMonth}
                onChange={e => setHistoryMonth(e.target.value)}
                style={{
                  padding: '9px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
                }}
              />
            </div>
          </div>

          {historyLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-muted)' }} />
            </div>
          ) : historyData ? (
            <>
              <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160, background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.25)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--accent-green)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Added this month</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', marginTop: 4 }}>{historyData.total_added}</div>
                </div>
                <div style={{ flex: 1, minWidth: 160, background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.25)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Used this month</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', marginTop: 4 }}>{historyData.total_used}</div>
                </div>
                <div style={{ flex: 1, minWidth: 160, background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.25)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: 0.4 }}>RTO units in delivered orders</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', marginTop: 4 }}>
                    {historyData.units_used_rto_delivered} <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>/ {historyData.total_units_delivered} units</span>
                    {historyData.total_units_delivered > 0 && (
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#f97316', marginLeft: 8 }}>
                        ({((historyData.units_used_rto_delivered / historyData.total_units_delivered) * 100).toFixed(1)}%)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    across {historyData.orders_delivered_used_rto} of {historyData.total_delivered} order{historyData.total_delivered !== 1 ? 's' : ''} delivered this month
                  </div>
                </div>
              </div>
              {historyData.total_delivered === 0 && (
                <div style={{ marginTop: -8, marginBottom: 20, fontSize: 11.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={12} style={{ flexShrink: 0 }} />
                  Delivery dates weren't tracked before today — this fills in as orders get delivered going forward.
                </div>
              )}

              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
                <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--bg-elevated)', zIndex: 1 }}>
                        <th style={{ textAlign: 'left', padding: '10px 18px', fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Date</th>
                        <th style={{ textAlign: 'right', padding: '10px 18px', fontSize: 11.5, fontWeight: 700, color: 'var(--accent-green)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Added</th>
                        <th style={{ textAlign: 'right', padding: '10px 18px', fontSize: 11.5, fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.days.map(day => (
                        <tr key={day.date} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '9px 18px', color: 'var(--text-primary)', fontWeight: 600 }}>
                            {new Date(day.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                          </td>
                          <td style={{ padding: '9px 18px', textAlign: 'right', color: day.added ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: day.added ? 700 : 400 }}>{day.added || '—'}</td>
                          <td style={{ padding: '9px 18px', textAlign: 'right', color: day.used ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: day.used ? 700 : 400 }}>{day.used || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>Couldn't load history.</div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* ═══ SCAN LABEL MODAL (Camera + Manual + Photo OCR) ═══ */}
      {/* ═══════════════════════════════════════════════════════ */}
      {showScan && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }} onClick={e => { if (e.target === e.currentTarget) closeScan(); }}>
          <div className="animate-in" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-xl)', padding: 0, width: 560, maxWidth: '95vw',
            boxShadow: 'var(--shadow-elevated)', maxHeight: '90vh', overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px 14px', borderBottom: '1px solid var(--border)',
              position: 'sticky', top: 0, background: 'var(--bg-card)',
              borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', zIndex: 2,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                    background: 'var(--accent-green-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><ScanLine size={18} color="var(--accent-green)" /></div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>Scan Shipping Label</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Barcode, photo OCR, or type order #</div>
                  </div>
                </div>
                <button onClick={closeScan} style={{
                  background: 'var(--bg-elevated)', border: 'none', borderRadius: 8,
                  width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}><X size={15} color="var(--text-muted)" /></button>
              </div>

              {/* Mode toggle — 3 modes now */}
              <div style={{ display: 'flex', gap: 4 }}>
                {([
                  { key: 'camera', icon: <ScanLine size={13} />, label: 'Barcode' },
                  { key: 'photo', icon: <Camera size={13} />, label: 'Photo OCR' },
                  { key: 'manual', icon: <Keyboard size={13} />, label: 'Type' },
                ] as const).map(mode => (
                  <button key={mode.key} onClick={() => {
                    if (scanMode === 'camera') stopCamera();
                    if (scanMode === 'photo') stopPhotoCamera();
                    setScanMode(mode.key);
                    setScanPreview(null);
                    setScanError('');
                    setOcrResult(null);
                    setOcrPreviewUrl('');
                    setOcrShowRawText(false);
                  }} style={{
                    flex: 1, padding: '8px 10px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    border: `1px solid ${scanMode === mode.key
                      ? mode.key === 'photo' ? 'rgba(251,146,60,0.3)' : mode.key === 'camera' ? 'rgba(45,212,160,0.3)' : 'rgba(79,125,249,0.3)'
                      : 'var(--border)'}`,
                    background: scanMode === mode.key
                      ? mode.key === 'photo' ? 'rgba(251,146,60,0.1)' : mode.key === 'camera' ? 'var(--accent-green-soft)' : 'var(--accent-blue-soft)'
                      : 'var(--bg-elevated)',
                    color: scanMode === mode.key
                      ? mode.key === 'photo' ? '#f97316' : mode.key === 'camera' ? 'var(--accent-green)' : 'var(--accent-blue)'
                      : 'var(--text-secondary)',
                  }}>
                    {mode.icon} {mode.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ padding: '16px 24px 24px' }}>

              {/* ═══ BARCODE CAMERA MODE ═══ */}
              {scanMode === 'camera' && !scanPreview && (
                <div style={{ marginBottom: 16 }}>
                  <div id={scannerContainerId} style={{
                    width: '100%', borderRadius: 'var(--radius-md)', overflow: 'hidden',
                    background: '#000', minHeight: 250,
                  }} />
                  {!cameraActive && !scanLoading && (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: 13 }}>
                      <Camera size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
                      <div>Starting camera...</div>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ PHOTO OCR MODE ═══ */}
              {scanMode === 'photo' && !ocrResult && !ocrLoading && (
                <div style={{ marginBottom: 16 }}>
                  {/* Live viewfinder or preview */}
                  {!ocrPreviewUrl ? (
                    <div style={{ position: 'relative' }}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                          width: '100%', borderRadius: 'var(--radius-md)',
                          background: '#000', minHeight: 220, objectFit: 'cover',
                          display: photoCameraActive ? 'block' : 'none',
                        }}
                      />
                      <canvas ref={canvasRef} style={{ display: 'none' }} />

                      {!photoCameraActive && (
                        <div style={{
                          background: '#000', borderRadius: 'var(--radius-md)', minHeight: 220,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8,
                          color: 'var(--text-muted)', fontSize: 13,
                        }}>
                          <Camera size={28} style={{ opacity: 0.3 }} />
                          <div>Starting camera...</div>
                        </div>
                      )}

                      {/* Capture + Upload buttons */}
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button onClick={capturePhoto} disabled={!photoCameraActive} style={{
                          flex: 1, padding: '12px 16px',
                          background: photoCameraActive ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'var(--bg-elevated)',
                          border: 'none', borderRadius: 'var(--radius-sm)',
                          color: photoCameraActive ? '#fff' : 'var(--text-muted)',
                          fontSize: 13, fontWeight: 700, cursor: photoCameraActive ? 'pointer' : 'default',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                        }}>
                          <Camera size={16} /> Capture Photo
                        </button>
                        <input type="file" ref={photoInputRef} accept="image/*" capture="environment" onChange={handlePhotoFileSelect} style={{ display: 'none' }} />
                        <button onClick={() => photoInputRef.current?.click()} style={{
                          padding: '12px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500,
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                          <ImageIcon size={14} /> Upload
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Show captured/uploaded image */
                    <div>
                      <img src={ocrPreviewUrl} alt="Label" style={{
                        width: '100%', borderRadius: 'var(--radius-md)', maxHeight: 300, objectFit: 'contain',
                        background: '#000',
                      }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button onClick={resetOCR} style={{
                          flex: 1, padding: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        }}><RotateCcw size={13} /> Retake</button>
                      </div>
                    </div>
                  )}

                  {/* Tip */}
                  <div style={{
                    marginTop: 12, padding: '10px 14px', background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.15)',
                    borderRadius: 'var(--radius-sm)', fontSize: 11.5, color: '#f97316', lineHeight: 1.5,
                  }}>
                    <strong>Tip:</strong> Hold the label flat and ensure the order number and barcode are clearly visible. Good lighting helps!
                  </div>
                </div>
              )}

              {/* ═══ MANUAL INPUT MODE ═══ */}
              {scanMode === 'manual' && !scanPreview && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px',
                    background: 'var(--bg-input)', border: '1px solid var(--accent-blue)',
                    borderRadius: 'var(--radius-sm)', boxShadow: '0 0 0 3px rgba(79,125,249,0.1)',
                  }}>
                    <ScanLine size={16} color="var(--accent-blue)" />
                    <input
                      ref={scanInputRef}
                      value={scanInput}
                      onChange={e => { setScanInput(e.target.value); setScanError(''); setScanResult(''); }}
                      onKeyDown={handleScanKeyDown}
                      placeholder="Scan barcode or type FS2046..."
                      autoFocus
                      style={{
                        background: 'transparent', border: 'none', color: 'var(--text-primary)',
                        fontSize: 15, fontWeight: 600, outline: 'none', width: '100%', padding: '12px 0',
                        fontFamily: 'inherit',
                      }}
                    />
                    {scanInput && (
                      <button onClick={resetScan} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0 }}>
                        <X size={14} color="var(--text-muted)" />
                      </button>
                    )}
                  </div>
                  <button onClick={handleScanLookup} disabled={!scanInput.trim() || scanLoading} style={{
                    padding: '12px 20px',
                    background: scanInput.trim() ? 'linear-gradient(135deg, var(--accent-green), #059669)' : 'var(--bg-elevated)',
                    border: 'none', borderRadius: 'var(--radius-sm)',
                    color: scanInput.trim() ? '#fff' : 'var(--text-muted)',
                    fontSize: 13, fontWeight: 600, cursor: scanInput.trim() ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {scanLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />}
                    Lookup
                  </button>
                </div>
              )}

              {/* ═══ OCR LOADING ═══ */}
              {ocrLoading && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '32px 0', gap: 12, color: 'var(--text-muted)',
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: 'rgba(251,146,60,0.1)', border: '2px solid rgba(251,146,60,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Loader2 size={22} color="#f97316" style={{ animation: 'spin 1s linear infinite' }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Processing label...</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Running OCR + barcode detection</div>
                </div>
              )}

              {/* ═══ BARCODE SCAN LOADING ═══ */}
              {scanLoading && scanMode !== 'photo' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Looking up order...
                </div>
              )}

              {/* Success message */}
              {scanResult && (
                <div style={{
                  background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.2)',
                  borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 13, color: 'var(--accent-green)', fontWeight: 600,
                }}><CheckCircle size={16} /> {scanResult}</div>
              )}

              {/* Error */}
              {scanError && (
                <div style={{
                  background: 'rgba(239,90,111,0.1)', border: '1px solid rgba(239,90,111,0.3)',
                  borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 13, color: 'var(--accent-red)', fontWeight: 500,
                }}><AlertTriangle size={16} /> {scanError}</div>
              )}

              {/* ═══ OCR RESULTS ═══ */}
              {ocrResult && (
                <div>
                  {/* Extracted fields card */}
                  <div style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
                    padding: '16px 18px', marginBottom: 14, border: '1px solid var(--border)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
                        Extracted from Label
                      </div>
                      {ocrResult.match_method && (
                        <span style={{
                          fontSize: 10, padding: '3px 8px', borderRadius: 20, fontWeight: 600, textTransform: 'uppercase',
                          background: 'var(--accent-green-soft)', color: 'var(--accent-green)',
                        }}>Matched by {ocrResult.match_method.replace('_', ' ')}</span>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                      {ocrResult.extracted?.order_number && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Hash size={14} color="#f97316" />
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Order</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-blue)' }}>{ocrResult.extracted.order_number}</div>
                          </div>
                        </div>
                      )}
                      {ocrResult.extracted?.awb_number && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Package size={14} color="#f97316" />
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>AWB</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{ocrResult.extracted.awb_number}</div>
                          </div>
                        </div>
                      )}
                      {ocrResult.extracted?.customer_name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Eye size={14} color="#f97316" />
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Customer</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{ocrResult.extracted.customer_name}</div>
                          </div>
                        </div>
                      )}
                      {ocrResult.extracted?.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Phone size={14} color="#f97316" />
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Phone</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{ocrResult.extracted.phone}</div>
                          </div>
                        </div>
                      )}
                      {ocrResult.extracted?.courier && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Truck size={14} color="#f97316" />
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Courier</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{ocrResult.extracted.courier}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {ocrResult.extracted?.address_lines?.length > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <MapPin size={14} color="#f97316" style={{ marginTop: 2, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Address</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            {ocrResult.extracted.address_lines.join(', ')}
                          </div>
                        </div>
                      </div>
                    )}

                    {ocrResult.barcodes?.length > 0 && (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>Barcodes Detected</div>
                        {ocrResult.barcodes.map((bc: any, i: number) => (
                          <div key={i} style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{bc.type}:</span> {bc.data}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Toggle raw OCR text */}
                    <button onClick={() => setOcrShowRawText(!ocrShowRawText)} style={{
                      marginTop: 10, background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 11, color: 'var(--text-muted)', textDecoration: 'underline', padding: 0,
                    }}>
                      {ocrShowRawText ? 'Hide' : 'Show'} raw OCR text
                    </button>
                    {ocrShowRawText && (
                      <pre style={{
                        marginTop: 8, padding: 12, background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)', fontSize: 10.5, color: 'var(--text-secondary)',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 150, overflowY: 'auto', lineHeight: 1.5,
                      }}>{ocrResult.ocr_text || '(no text extracted)'}</pre>
                    )}
                  </div>

                  {/* Order match preview (same as barcode scan) */}
                  {ocrResult.order_match ? (
                    <div>
                      <div style={{
                        background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
                        padding: '16px 18px', marginBottom: 14, border: '1px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent-blue)' }}>{ocrResult.order_match.order_name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{ocrResult.order_match.customer_name}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>₹{(ocrResult.order_match.total || 0).toLocaleString('en-IN')}</div>
                            <span style={{
                              fontSize: 10.5, padding: '2px 8px', borderRadius: 20, fontWeight: 600, textTransform: 'uppercase',
                              background: ocrResult.order_match.payment_method === 'cod' ? 'var(--accent-amber-soft)' : 'var(--accent-blue-soft)',
                              color: ocrResult.order_match.payment_method === 'cod' ? 'var(--accent-amber)' : 'var(--accent-blue)',
                            }}>{ocrResult.order_match.payment_method}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, fontSize: 11.5, color: 'var(--text-muted)' }}>
                          <span>Status: <strong style={{ color: 'var(--text-secondary)' }}>{ocrResult.order_match.order_status}</strong></span>
                          <span>{ocrResult.order_match.items_count} item{ocrResult.order_match.items_count !== 1 ? 's' : ''}</span>
                          {ocrResult.order_match.province && <span>{ocrResult.order_match.province}</span>}
                        </div>
                      </div>

                      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>
                        Products to Add as RTO
                      </div>

                      {ocrResult.order_match.items.map((item: any, i: number) => (
                        <div key={i} style={{
                          background: item.already_added ? 'var(--bg-elevated)' : 'var(--accent-green-soft)',
                          border: `1px solid ${item.already_added ? 'var(--border)' : 'rgba(45,212,160,0.15)'}`,
                          borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 8,
                          opacity: item.already_added ? 0.5 : 1,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.product_title}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                              Size: <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{item.size}</span>
                              {item.color && <> · Color: {item.color}</>}
                            </div>
                          </div>
                          {item.already_added && <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 600 }}>Already added</span>}
                        </div>
                      ))}

                      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                        {ocrResult.order_match.items.some((i: any) => !i.already_added) ? (
                          <button onClick={handleOCRConfirm} disabled={scanConfirming} style={{
                            flex: 1, padding: 13,
                            background: 'linear-gradient(135deg, var(--accent-green), #059669)',
                            border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff',
                            fontSize: 14, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          }}>
                            {scanConfirming ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={15} />}
                            {scanConfirming ? 'Adding...' : `Add ${ocrResult.order_match.items.filter((i: any) => !i.already_added).length} to RTO`}
                          </button>
                        ) : (
                          <div style={{ flex: 1, textAlign: 'center', padding: 13, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--text-muted)' }}>
                            All items already in RTO
                          </div>
                        )}
                        <button onClick={resetOCR} style={{
                          padding: '13px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        }}>Scan Next</button>
                      </div>
                    </div>
                  ) : (
                    /* No match found — offer manual input */
                    <div style={{
                      background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
                      padding: '20px', border: '1px solid var(--border)', textAlign: 'center',
                    }}>
                      <AlertTriangle size={24} color="var(--accent-amber)" style={{ marginBottom: 8 }} />
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>No order match found</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                        {ocrResult.extracted?.order_number
                          ? `Order "${ocrResult.extracted.order_number}" not found in database.`
                          : 'Could not extract an order number from the label.'}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={resetOCR} style={{
                          flex: 1, padding: '11px', background: 'linear-gradient(135deg, #f97316, #ea580c)',
                          border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}><RotateCcw size={13} style={{ marginRight: 6 }} /> Try Again</button>
                        <button onClick={() => { setScanMode('manual'); setOcrResult(null); setOcrPreviewUrl(''); }} style={{
                          flex: 1, padding: '11px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        }}>Type Manually</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ BARCODE/MANUAL PREVIEW (existing) ═══ */}
              {scanPreview && scanMode !== 'photo' && (
                <div>
                  <div style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
                    padding: '16px 18px', marginBottom: 14, border: '1px solid var(--border)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent-blue)' }}>{scanPreview.order_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{scanPreview.customer_name}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>₹{(scanPreview.total || 0).toLocaleString('en-IN')}</div>
                        <span style={{
                          fontSize: 10.5, padding: '2px 8px', borderRadius: 20, fontWeight: 600, textTransform: 'uppercase',
                          background: scanPreview.payment_method === 'cod' ? 'var(--accent-amber-soft)' : 'var(--accent-blue-soft)',
                          color: scanPreview.payment_method === 'cod' ? 'var(--accent-amber)' : 'var(--accent-blue)',
                        }}>{scanPreview.payment_method}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 11.5, color: 'var(--text-muted)' }}>
                      <span>Status: <strong style={{ color: 'var(--text-secondary)' }}>{scanPreview.order_status}</strong></span>
                      <span>{scanPreview.items_count} item{scanPreview.items_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>
                    Products to Add as RTO
                  </div>

                  {scanPreview.items.map((item: any, i: number) => {
                    const key = `${i}`;
                    const isChecked = selectedScanItems.has(key);
                    return (
                      <div key={i} onClick={() => {
                        if (item.already_added) return;
                        setSelectedScanItems(prev => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        });
                      }} style={{
                        background: item.already_added ? 'var(--bg-elevated)' : isChecked ? 'var(--accent-green-soft)' : 'var(--bg-elevated)',
                        border: `1px solid ${item.already_added ? 'var(--border)' : isChecked ? 'rgba(45,212,160,0.15)' : 'var(--border)'}`,
                        borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 8,
                        opacity: item.already_added ? 0.5 : 1,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        cursor: item.already_added ? 'default' : 'pointer',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={item.already_added}
                            onChange={() => {}}
                            style={{ width: 16, height: 16, accentColor: 'var(--accent-green)', cursor: item.already_added ? 'default' : 'pointer' }}
                          />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.product_title}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                              Size: <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{item.size}</span>
                              {item.color && <> · Color: {item.color}</>}
                            </div>
                          </div>
                        </div>
                        {item.already_added && <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 600 }}>Already added</span>}
                      </div>
                    );
                  })}

                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    {scanPreview.items.some((i: any) => !i.already_added) ? (
                      <button onClick={handleScanConfirm} disabled={scanConfirming || selectedScanItems.size === 0} style={{
                        flex: 1, padding: 13,
                        background: selectedScanItems.size > 0 ? 'linear-gradient(135deg, var(--accent-green), #059669)' : 'var(--bg-elevated)',
                        border: selectedScanItems.size > 0 ? 'none' : '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)', color: selectedScanItems.size > 0 ? '#fff' : 'var(--text-muted)',
                        fontSize: 14, fontWeight: 700, cursor: selectedScanItems.size > 0 ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}>
                        {scanConfirming ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={15} />}
                        {scanConfirming ? 'Adding...' : selectedScanItems.size === 0 ? 'Select items to add' : `Add ${selectedScanItems.size} to RTO`}
                      </button>
                    ) : (
                      <div style={{ flex: 1, textAlign: 'center', padding: 13, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--text-muted)' }}>
                        All items already in RTO
                      </div>
                    )}
                    <button onClick={resetScan} style={{
                      padding: '13px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                    }}>Scan Next</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {assignItem && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }} onClick={e => { if (e.target === e.currentTarget) setAssignItem(null); }}>
          <div className="animate-in" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-xl)', padding: 0, width: 500, maxWidth: '95vw',
            boxShadow: 'var(--shadow-elevated)', maxHeight: '80vh', overflowY: 'auto',
          }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>Assign RTO to Order</div>
                <button onClick={() => setAssignItem(null)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <X size={15} color="var(--text-muted)" />
                </button>
              </div>
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', display: 'flex', gap: 16, fontSize: 12.5, flexWrap: 'wrap' }}>
                <div><span style={{ color: 'var(--text-muted)' }}>Article:</span> <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{assignItem.article_type}</span></div>
                <div><span style={{ color: 'var(--text-muted)' }}>Color:</span> <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{assignItem.color}</span></div>
                <div><span style={{ color: 'var(--text-muted)' }}>Size:</span> <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{assignItem.size}</span></div>
              </div>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 12 }}>Matching Pending Orders</div>
              {suggestLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Finding matches...
                </div>
              ) : suggestions.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                  {suggestions.map((s: any, i: number) => (
                    <div key={i} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-blue)' }}>{s.order_name}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{s.customer_name} · {s.product} · {s.variant}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleAssign(s.order_name)} disabled={assigning} style={{
                          padding: '7px 14px', background: 'linear-gradient(135deg, var(--accent-green), #059669)',
                          border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}>{assigning ? '...' : 'Assign'}</button>
                        <button onClick={(e) => { e.stopPropagation(); handleIgnoreOrder(s.order_name); }} style={{
                          padding: '7px 12px', background: 'rgba(239,90,111,0.1)', border: '1px solid rgba(239,90,111,0.3)',
                          borderRadius: 6, color: '#ef5a6f', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                          whiteSpace: 'nowrap', zIndex: 10, position: 'relative',
                        }}>Skip</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 13, marginBottom: 16, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>No matching pending orders found.</div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>Or Assign Manually</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={manualOrderId} onChange={e => setManualOrderId(e.target.value)}
                  placeholder="e.g. FS2067" onKeyDown={e => { if (e.key === 'Enter' && manualOrderId) handleAssign(manualOrderId); }}
                  style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
                <button onClick={() => handleAssign(manualOrderId)} disabled={!manualOrderId || assigning} style={{
                  padding: '10px 18px', background: manualOrderId ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'var(--bg-elevated)',
                  border: 'none', borderRadius: 'var(--radius-sm)', color: manualOrderId ? '#fff' : 'var(--text-muted)',
                  fontSize: 13, fontWeight: 600, cursor: manualOrderId ? 'pointer' : 'default',
                }}>Assign</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* ═══ ADD MANUALLY MODAL (search design → pick size) ═══ */}
      {/* ═══════════════════════════════════════════════════════ */}
      {showManualAdd && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }} onClick={e => { if (e.target === e.currentTarget) closeManualAdd(); }}>
          <div className="animate-in" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-xl)', padding: 0, width: 480, maxWidth: '95vw',
            boxShadow: 'var(--shadow-elevated)', maxHeight: '85vh', overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px 14px', borderBottom: '1px solid var(--border)',
              position: 'sticky', top: 0, background: 'var(--bg-card)',
              borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', zIndex: 2,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                  background: 'var(--accent-green-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><PlusCircle size={18} color="var(--accent-green)" /></div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>Add RTO Stock</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                    {manualSelectedDesign ? manualSelectedDesign.shopify_title : 'Search a design, then pick a size'}
                  </div>
                </div>
              </div>
              <button onClick={closeManualAdd} style={{
                background: 'var(--bg-elevated)', border: 'none', borderRadius: 8,
                width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}><X size={15} color="var(--text-muted)" /></button>
            </div>

            <div style={{ padding: 20 }}>
              {!manualSelectedDesign ? (
                <>
                  {/* Step 1: search design name */}
                  <div style={{ position: 'relative', marginBottom: 14 }}>
                    <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      autoFocus
                      value={manualDesignQuery}
                      onChange={e => setManualDesignQuery(e.target.value)}
                      placeholder="Search design name..."
                      style={{
                        width: '100%', padding: '10px 14px 10px 36px', background: 'var(--bg-input)',
                        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                        color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {manualDesignLoading ? (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>
                      <Loader2 size={20} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : filteredManualDesigns.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
                      {filteredManualDesigns.map(d => (
                        <button key={d.id} onClick={() => selectManualDesign(d)} style={{
                          textAlign: 'left', padding: '10px 12px', background: 'var(--bg-elevated)',
                          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{d.shopify_title}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{d.blank_article} · {d.blank_color}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                      No matching designs found.
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Step 2: pick a size */}
                  <button onClick={() => { setManualSelectedDesign(null); setManualSizeOptions([]); }} style={{
                    background: 'none', border: 'none', color: 'var(--accent-blue)', fontSize: 12.5,
                    fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 5,
                  }}>← Back to search</button>

                  {manualSizeLoading ? (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>
                      <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      {manualSizeOptions.map(opt => {
                        const added = manualJustAdded[opt.size] || 0;
                        const busy = manualAddingSize === opt.size;
                        return (
                          <button key={opt.size} onClick={() => handleManualAddSize(opt.size)} disabled={busy} style={{
                            position: 'relative', padding: '14px 10px', background: added > 0 ? 'var(--accent-green-soft)' : 'var(--bg-elevated)',
                            border: `1px solid ${added > 0 ? 'var(--accent-green)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)',
                            cursor: busy ? 'default' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                          }}>
                            {busy ? (
                              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} color="var(--text-muted)" />
                            ) : (
                              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{opt.size}</span>
                            )}
                            <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{opt.quantity} in stock</span>
                            {added > 0 && (
                              <span style={{
                                position: 'absolute', top: -6, right: -6, background: 'var(--accent-green)', color: '#fff',
                                fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 6px',
                              }}>+{added}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 14, textAlign: 'center' }}>
                    Tap a size to add one unit. Tap again for more.
                  </div>

                  {Object.keys(manualJustAdded).length > 0 && (
                    <button onClick={handleAddNextDesign} style={{
                      width: '100%', marginTop: 16, padding: '13px 16px',
                      background: 'linear-gradient(135deg, var(--accent-green), #059669)',
                      border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 14,
                      fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}><PlusCircle size={16} /> Add Next Design</button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        #${scannerContainerId} { position: relative; max-height: 300px; overflow: hidden; border-radius: 12px; }
        #${scannerContainerId} video { border-radius: 12px; width: 100% !important; max-height: 300px; object-fit: cover; }
        #${scannerContainerId} img[alt="Info icon"] { display: none; }
        #${scannerContainerId} br { display: none; }
        #${scannerContainerId} span { display: none; }
        @keyframes rto-glow {
          0%, 100% { box-shadow: inset 0 0 0 1px rgba(45,212,160,0.15); }
          50% { box-shadow: inset 0 0 0 1px rgba(45,212,160,0.4), inset 0 0 20px rgba(45,212,160,0.06); }
        }
        .rto-match-glow { animation: rto-glow 2.5s ease infinite; }
        .rto-match-glow:hover { background: rgba(45,212,160,0.12) !important; }
        @keyframes subtle-bounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
      `}</style>
    </div>
    </Layout>
  );
};

export default RTO;
